/* ============================================
   Pantalla de entrada — dos pasos
   Paso 1: correo    → POST /api/auth/check-email
   Paso 2: contraseña → POST /api/auth/login
   Tope de intentos: 3, y 15 minutos de bloqueo.

   Es la MISMA pantalla para los tres accesos (ERP, Clientes y Partners); a
   donde va cada uno lo decide el rol que devuelve el login, no el diseño. El
   marco (foto, fondo difuminado, modal) vive en LoginShell.
   ============================================ */

import { useState, useEffect, useCallback, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { showToast } from '@/components/layout/Toast'
import QRLoginDisplay from '@/components/auth/QRLoginDisplay'
import LoginShell from '@/components/auth/LoginShell'
import { consumirAvisoSesionExpirada } from '@/lib/sesion'
import { dominioDelPanel, esPanelAdmin, type Panel } from '@/lib/domain'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

function ErrorIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  )
}

type LoginStep = 'email' | 'password'

/**
 * El correo viaja al otro dominio en el FRAGMENTO (`#correo=`) y no en la
 * query: el fragmento no se manda en la peticion HTTP, asi que el correo no
 * queda en el log de ningun servidor ni en la cabecera `Referer`. Igual se
 * borra de la barra en cuanto se lee.
 */
const CLAVE_HANDOFF = 'correo'

function correoDelHash(): string | null {
  const m = new RegExp(`(?:^#|&)${CLAVE_HANDOFF}=([^&]+)`).exec(window.location.hash)
  if (!m) return null
  try { return decodeURIComponent(m[1]) } catch { return null }
}

export default function Login() {
  const [step, setStep] = useState<LoginStep>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [forgotMode, setForgotMode] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)
  const [attemptsRemaining, setAttemptsRemaining] = useState(3)
  const [blockedMinutes, setBlockedMinutes] = useState(0)
  const { signIn, resetPassword } = useAuth()
  const navigate = useNavigate()
  const [loginMode, setLoginMode] = useState<'password' | 'qr'>('password')
  // Llegamos desde el otro dominio con el correo ya resuelto. Es el freno del
  // rebote: si por lo que sea el panel volviera a no coincidir, acá NO se
  // redirige otra vez — se pide la contraseña y listo.
  const [vinoDeOtroDominio, setVinoDeOtroDominio] = useState(false)

  // Si llegamos acá porque el token vencía, decirlo: sin esto el usuario ve el
  // login sin explicación y cree que el sistema lo expulsó por un error.
  useEffect(() => {
    if (consumirAvisoSesionExpirada()) {
      setError('Tu sesión expiró. Ingresá de nuevo para continuar.')
    }
  }, [])

  /**
   * Paso 1: resolver el correo. Además de existencia y bloqueo, el backend
   * dice en qué panel vive la cuenta, y si no es este dominio se salta al
   * correcto **antes** de pedir la contraseña.
   *
   * Ese orden es todo el arreglo. El token se guarda en `localStorage`, que es
   * por origen: entrando con una cuenta de admin desde `client.1conecta.com`
   * la sesión nacía en el origen equivocado, el guard mandaba al otro dominio
   * —donde no había token— y la pantalla parpadeaba para terminar otra vez en
   * el login, vacío. Decidiendo acá, la sesión nace donde tiene que vivir.
   */
  const comprobarCorreo = useCallback(async (correo: string, sinRedirigir = false) => {
    if (!correo) return
    setError(null)
    setLoading(true)

    try {
      const res = await fetch(`${API_URL}/api/auth/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: correo }),
      })
      const data = await res.json()

      if (!data.exists) {
        setError('No existe una cuenta con este correo electrónico.')
        setLoading(false)
        return
      }

      if (!sinRedirigir && data.panel) {
        const destino = dominioDelPanel(data.panel as Panel)
        if (destino) {
          // Sin `setLoading(false)`: el spinner se queda puesto hasta que el
          // navegador cambie de página, así no asoma el paso de contraseña.
          window.location.replace(
            `${destino}/login#${CLAVE_HANDOFF}=${encodeURIComponent(correo)}`,
          )
          return
        }
      }

      if (data.blocked) {
        setBlockedMinutes(data.block_minutes_remaining)
        setError(`Cuenta bloqueada. Intenta de nuevo en ${data.block_minutes_remaining} minuto${data.block_minutes_remaining > 1 ? 's' : ''}.`)
        setLoading(false)
        return
      }

      setAttemptsRemaining(data.attempts_remaining)
      setStep('password')
    } catch {
      setError('Error de conexión. Intenta de nuevo.')
    }
    setLoading(false)
  }, [])

  async function handleCheckEmail(e: FormEvent) {
    e.preventDefault()
    // `vinoDeOtroDominio` frena el rebote: si ya nos mandaron acá con este
    // correo, no se vuelve a saltar. Se apaga en cuanto el usuario escribe
    // otro, que sí puede ser de un panel distinto.
    await comprobarCorreo(email, vinoDeOtroDominio)
  }

  // Veníamos del otro dominio: el correo llega en el fragmento, se borra de la
  // barra y se salta derecho a la contraseña.
  useEffect(() => {
    const correo = correoDelHash()
    if (!correo) return
    history.replaceState(null, '', window.location.pathname + window.location.search)
    setEmail(correo)
    setVinoDeOtroDominio(true)
    comprobarCorreo(correo, true)
  }, [comprobarCorreo])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const { multi, role } = await signIn(email, password)
      if (multi) {
        // La identidad tiene varias cuentas → elegir cuál
        navigate('/seleccionar-cuenta', { replace: true })
      } else if (role === 'partner') {
        navigate('/partner/pipeline', { replace: true })
      } else if (esPanelAdmin(role)) {
        // Al ERP siempre, sin mirar el dominio. La condición vieja era
        // `role === 'cliente' || isPortalDomain`, así que un admin que entraba
        // desde el dominio de clientes caía en `/portal`, y de ahí arrancaba
        // el rebote entre los dos dominios.
        //
        // Un rol recortado entra igual por la raíz: quién aterriza dónde lo
        // decide `ModuloGuard` con los módulos ya cargados, en un solo lugar,
        // en vez de repetir esa cuenta acá con lo poco que sabe el login.
        navigate('/', { replace: true })
      } else {
        navigate('/portal', { replace: true })
      }
    } catch (err: any) {
      const msg = err?.message || 'Error de autenticación'

      // Parse rate-limit info from error
      if (msg.includes('bloqueada')) {
        const minMatch = msg.match(/(\d+)\s*minuto/)
        setBlockedMinutes(minMatch ? parseInt(minMatch[1]) : 15)
        setError(msg)
      } else if (msg.includes('intentos restantes') || msg.includes('intento restante')) {
        const remMatch = msg.match(/(\d+)\s*intento/)
        setAttemptsRemaining(remMatch ? parseInt(remMatch[1]) : 0)
        setError(msg)
      } else {
        setError('Credenciales incorrectas. Verifica tu contraseña.')
      }
    } finally {
      setLoading(false)
    }
  }

  async function handleForgotPassword(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await resetPassword(email)
      setForgotSent(true)
      showToast('Se envió un enlace de recuperación a tu correo', 'success')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al enviar correo de recuperación')
    } finally {
      setLoading(false)
    }
  }

  function goBack() {
    setStep('email')
    setPassword('')
    setError(null)
  }

  /* ── Portal login form fragment (reused in portal + admin layouts) ── */
  const loginFormContent = forgotMode ? (
    <>
      {error && <div className="login-error" role="alert"><ErrorIcon />{error}</div>}
      {forgotSent ? (
        <div className="portal-login-success-msg">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,7 12,13 2,7"/></svg>
          <p className="portal-login-success-title">Correo enviado</p>
          <p className="portal-login-success-text">Revisa tu bandeja de entrada y haz clic en el enlace para restablecer tu contraseña.</p>
        </div>
      ) : (
        <>
          <p className="portal-login-forgot-desc">Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.</p>
          <div className="portal-login-field">
            <label htmlFor="forgot-email">Email</label>
            <input id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@empresa.com" required autoComplete="email" autoFocus />
          </div>
          <button type="submit" className="portal-login-submit" disabled={loading || !email} id="forgot-submit">
            {loading ? <span className="login-spinner" /> : 'Enviar Enlace'}
          </button>
        </>
      )}
      <button type="button" className="portal-login-link" onClick={() => { setForgotMode(false); setForgotSent(false); setError(null) }}>
        ← Volver al inicio de sesión
      </button>
    </>
  ) : step === 'email' ? (
    <>
      {error && <div className="login-error" role="alert"><ErrorIcon />{error}</div>}
      <div className="portal-login-field">
        <label htmlFor="login-email">Correo electrónico</label>
        <input id="login-email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setVinoDeOtroDominio(false) }} placeholder="tu@empresa.com" required autoComplete="email" autoFocus />
      </div>
      <button type="submit" className="portal-login-submit" disabled={loading || !email} id="login-submit">
        {loading ? <span className="login-spinner" /> : 'Continuar'}
      </button>
      <button type="button" className="portal-login-link" onClick={() => { setForgotMode(true); setError(null) }}>
        ¿Olvidaste tu contraseña?
      </button>
    </>
  ) : (
    <>
      {error && <div className="login-error" role="alert"><ErrorIcon />{error}</div>}
      {/* Show blocked timer */}
      {blockedMinutes > 0 && (
        <div className="portal-login-blocked">
          ⏳ Cuenta bloqueada por {blockedMinutes} minuto{blockedMinutes > 1 ? 's' : ''}. Intenta de nuevo después.
        </div>
      )}
      {/* El correo NO se repite acá: ya está bajo el título, con su botón de
          «Cambiar». Un campo de sólo lectura con el mismo dato dos centímetros
          más abajo ocupa el lugar de un campo que sí se puede llenar. */}
      {/* Intentos restantes */}
      {attemptsRemaining < 3 && (
        <div className={`portal-login-attempts${attemptsRemaining <= 1 ? ' is-critical' : ''}`}>
          {attemptsRemaining > 0 ? `${attemptsRemaining} intento${attemptsRemaining > 1 ? 's' : ''} restante${attemptsRemaining > 1 ? 's' : ''}` : 'Sin intentos restantes'}
        </div>
      )}
      <div className="portal-login-field">
        <label htmlFor="login-password">Contraseña</label>
        <input id="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required autoComplete="current-password" autoFocus />
      </div>
      <button type="submit" className="portal-login-submit" disabled={loading || !password || blockedMinutes > 0} id="login-submit">
        {loading ? <span className="login-spinner" /> : 'Iniciar Sesión'}
      </button>
      <button type="button" className="portal-login-link" onClick={() => { setForgotMode(true); setError(null) }}>
        ¿Olvidaste tu contraseña?
      </button>
    </>
  )

  /* ─── Una sola pantalla para los tres accesos ───────────────────────────
     Antes había dos: el portal se llevaba un panel con foto y el ERP (y con
     el, Partners) una tarjeta centrada y pelada. Dos diseños para el mismo
     formulario es el doble de trabajo y la mitad del cuidado en cada uno. */
  const alEnviar = forgotMode ? handleForgotPassword : step === 'email' ? handleCheckEmail : handleSubmit

  const titulo = forgotMode
    ? 'Recuperar acceso'
    : step === 'password' ? 'Ingresa tu contraseña' : 'Bienvenido de vuelta'

  // En el paso 2 el subtítulo es el correo, con su botón de volver: confirma
  // con qué cuenta se está entrando y deja corregirlo sin gastar un campo de
  // sólo lectura en repetir el mismo dato.
  const subtitulo = forgotMode
    ? 'Te mandamos un enlace para volver a entrar'
    : step === 'password'
      ? <>{email} <button type="button" className="login-panel__cambiar" onClick={goBack}>Cambiar</button></>
      : 'Ingresa tu correo para continuar'

  return (
    <LoginShell
      pie={<>&copy; 2026 1Conecta</>}
      /* El QR es una salida alternativa, no la mitad de la pantalla: era una
         barra de pestañas de 375x45 —del mismo tamaño que el campo y que el
         botón— para la forma de entrar que casi nadie usa. Ahora vive en el
         pie, que estaba vacío.

         Sólo en el primer paso: con el correo ya puesto, cambiar a QR descarta
         ese dato y hace volver al principio. */
      accion={step === 'email' && !forgotMode && loginMode === 'password' ? (
        <button type="button" className="portal-login-link login-panel__alterno" onClick={() => setLoginMode('qr')} id="tab-qr">
          Entrar con código QR
        </button>
      ) : undefined}
    >
      <div className="login-panel__header">
        <h1 id="login-titulo">{titulo}</h1>
        <p>{subtitulo}</p>
      </div>

      <form onSubmit={alEnviar} className="portal-login-form" id="login-form">
        {loginMode === 'qr' && step === 'email' && !forgotMode ? (
          <QRLoginDisplay onSwitchToPassword={() => setLoginMode('password')} isPortal />
        ) : (
          <>
            {loginFormContent}
          </>
        )}
      </form>
    </LoginShell>
  )
}
